import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { history } from "../../../../helpers/history";
import {
	hybridGetDepositChannels,
	hybridGetReceiverBankAccount,
	hybridPlayerSubmitDeposit,
	hybridPlayerSubmitDepositFPay,
	hybridPlayerSubmitDepositGateway,
	hybridPlayerSubmitDepositOnempay,
	HYBRID_DEPOSIT_METHOD_BANK_TRANSFER,
	HYBRID_DEPOSIT_METHOD_ONEMPAY,
	type HybridDepositChannel,
	type HybridReceiverBankAccountRow,
} from "../../../../services/hybridPlayer.service";
import { uploadHybridDepositReceiptToS3 } from "../../../../services/upload.service";
import { getHybridPlayerIdFromUserToken } from "../../../../utils/hybridSession";
import {
	formatReceiverBankDisplayFromApi,
	getDepositChannelLabelKey,
	getFpaySubmitMethodTypeId,
	isAllowedReceiptFile,
	isFpayRedirectDepositChannel,
	isGenericGatewayDepositChannel,
	MAX_RECEIPT_BYTES,
	parseHybridDepositConfirmState,
	parseHybridReceiverBankAccountResponse,
	RECEIPT_ACCEPT,
	type HybridDepositConfirmState,
} from "./depositShared";

const HybridDepositConfirmPage: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const [draft, setDraft] = useState<HybridDepositConfirmState | null>(null);
	const [receiver, setReceiver] = useState<HybridReceiverBankAccountRow | null>(
		null
	);
	const [receiverLoading, setReceiverLoading] = useState(false);
	const [receiverError, setReceiverError] = useState<string | null>(null);
	const [promoCode, setPromoCode] = useState("");
	const [receiptFile, setReceiptFile] = useState<File | null>(null);
	const [receiptPreviewUrl, setReceiptPreviewUrl] = useState<string | null>(
		null
	);
	const receiptFileInputRef = useRef<HTMLInputElement>(null);
	const [submitLoading, setSubmitLoading] = useState(false);
	// Banks to pick from, for a gateway channel that needs one (JGO FPX).
	const [payerBanks, setPayerBanks] = useState<
		{ code: string; name: string }[]
	>([]);
	const [payerBanksLoading, setPayerBanksLoading] = useState(false);
	const [payerBankCode, setPayerBankCode] = useState("");

	useEffect(() => {
		const parsed = parseHybridDepositConfirmState(location.state);
		if (!parsed) {
			history.replace("/deposit");
			return;
		}
		setDraft(parsed);
	}, [location.state]);

	useEffect(() => {
		if (!draft || draft.depositChannel !== "bank_transfer") return;

		let cancelled = false;
		(async () => {
			setReceiverLoading(true);
			setReceiverError(null);
			setReceiver(null);
			try {
				const res: any = await hybridGetReceiverBankAccount({
					amount: draft.amount,
				});
				if (cancelled) return;

				if (!res?.status) {
					setReceiverError(
						res?.message || t("No receiver bank account is available.")
					);
					return;
				}

				const row = parseHybridReceiverBankAccountResponse(res.data);
				if (!row) {
					setReceiverError(t("No receiver bank account is available."));
					return;
				}
				setReceiver(row);
			} catch (error: any) {
				if (!cancelled) {
					setReceiverError(
						error?.response?.data?.message ||
							t("Failed to load receiver bank account. Please try again.")
					);
				}
			} finally {
				if (!cancelled) setReceiverLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [draft, t]);

	// A gateway channel lists its banks in hybridGetDepositChannels when the
	// player must pick one before paying (JGO FPX).
	useEffect(() => {
		if (!draft || !isGenericGatewayDepositChannel(draft.depositChannel)) {
			return;
		}
		const methodTypeId = getFpaySubmitMethodTypeId(draft.depositChannel);

		let cancelled = false;
		(async () => {
			setPayerBanksLoading(true);
			try {
				const res: any = await hybridGetDepositChannels();
				if (cancelled) return;
				const channels: HybridDepositChannel[] = Array.isArray(res?.data)
					? res.data
					: [];
				const channel = channels.find(
					(ch) => Number(ch.method_type_id) === methodTypeId
				);
				setPayerBanks(channel?.banks ?? []);
			} catch {
				if (!cancelled) setPayerBanks([]);
			} finally {
				if (!cancelled) setPayerBanksLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [draft]);

	useEffect(() => {
		return () => {
			if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
		};
	}, [receiptPreviewUrl]);

	const clearReceiptFile = () => {
		setReceiptFile(null);
		if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
		setReceiptPreviewUrl(null);
		if (receiptFileInputRef.current) receiptFileInputRef.current.value = "";
	};

	const handleReceiptFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			clearReceiptFile();
			return;
		}
		if (!isAllowedReceiptFile(file)) {
			toast.error(t("Please upload a PDF, JPG, or PNG receipt"));
			e.target.value = "";
			clearReceiptFile();
			return;
		}
		if (file.size > MAX_RECEIPT_BYTES) {
			toast.error(t("Receipt file must be 10 MB or smaller"));
			e.target.value = "";
			clearReceiptFile();
			return;
		}
		if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
		setReceiptFile(file);
		if (file.type === "image/jpeg" || file.type === "image/png") {
			setReceiptPreviewUrl(URL.createObjectURL(file));
		} else if (
			file.type === "application/pdf" ||
			file.name.toLowerCase().endsWith(".pdf")
		) {
			setReceiptPreviewUrl(null);
		} else {
			const lower = file.name.toLowerCase();
			if (
				lower.endsWith(".png") ||
				lower.endsWith(".jpg") ||
				lower.endsWith(".jpeg")
			) {
				setReceiptPreviewUrl(URL.createObjectURL(file));
			} else {
				setReceiptPreviewUrl(null);
			}
		}
	};

	if (!draft) {
		return (
			<section className="main-height">
				<div className="dashboard-wrapper hybrid-deposit-page" />
			</section>
		);
	}

	const isBankTransfer = draft.depositChannel === "bank_transfer";
	const isFpayRedirect = isFpayRedirectDepositChannel(draft.depositChannel);
	const channelLabel = t(getDepositChannelLabelKey(draft.depositChannel));

	const bankDisplayName = receiver
		? formatReceiverBankDisplayFromApi(receiver)
		: "";

	const handleSubmitDeposit = async () => {
		if (!receiver) {
			toast.error(
				receiverError || t("Receiver bank account is not available.")
			);
			return;
		}
		if (!receiptFile) {
			toast.error(t("Please upload a receipt (PDF, JPG, or PNG)"));
			return;
		}

		const remarkParts: string[] = [];
		if (promoCode.trim()) {
			remarkParts.push(`promo:${promoCode.trim()}`);
		}
		const remark = remarkParts.join(" ").slice(0, 255);

		const receiverBankName = String(receiver.receiver_bank_name ?? "").trim();
		const receiverBankAccNo = String(
			receiver.receiver_bank_acc_no ?? ""
		).trim();
		const receiverBankAccName = String(
			receiver.receiver_bank_acc_name ?? ""
		).trim();
		if (!receiverBankName || !receiverBankAccNo || !receiverBankAccName) {
			toast.error(t("Receiver account details are incomplete."));
			return;
		}

		setSubmitLoading(true);
		try {
			const uploadResult = await uploadHybridDepositReceiptToS3(receiptFile);
			if (!uploadResult || !uploadResult.url?.trim()) {
				const idMissing = getHybridPlayerIdFromUserToken() == null;
				toast.error(
					idMissing
						? t("Session is invalid. Please sign in again.")
						: t("Failed to upload receipt. Please try again.")
				);
				return;
			}
			const uploadedReceipt = uploadResult.url.trim();

			const res: any = await hybridPlayerSubmitDeposit({
				amount: draft.amount,
				method_type_id: HYBRID_DEPOSIT_METHOD_BANK_TRANSFER,
				uploaded_receipt: uploadedReceipt,
				receiver_bank_name: receiverBankName,
				receiver_bank_acc_no: receiverBankAccNo,
				receiver_bank_acc_name: receiverBankAccName,
				remark: remark || undefined,
				player_bonus_id: draft.playerBonusId ?? undefined,
				bonus_config_id: draft.bonusConfigId ?? undefined,
			});
			if (res?.status) {
				toast.success(res?.message || t("Submit successfully"));
				clearReceiptFile();
				history.replace("/deposit");
			} else {
				toast.error(
					res?.message || t("Action Failed. Please try again later.")
				);
			}
		} catch (error: any) {
			toast.error(
				error?.response?.data?.message ||
					error?.message ||
					t("Action Failed. Please try again later.")
			);
		} finally {
			setSubmitLoading(false);
		}
	};

	const submitDisabled =
		submitLoading || receiverLoading || !receiver || Boolean(receiverError);

	const needsPayerBank = payerBanks.length > 0;

	const handleFpayConfirm = async () => {
		if (!draft) return;
		if (needsPayerBank && !payerBankCode) {
			toast.error(t("Please select your bank"));
			return;
		}
		setSubmitLoading(true);
		try {
			const isOnempay = draft.depositChannel === "onempay";
			const isGenericGateway = isGenericGatewayDepositChannel(
				draft.depositChannel
			);
			const res: any = isOnempay
				? await hybridPlayerSubmitDepositOnempay({
						amount: draft.amount,
						method_type_id: HYBRID_DEPOSIT_METHOD_ONEMPAY,
						player_bonus_id: draft.playerBonusId ?? undefined,
						bonus_config_id: draft.bonusConfigId ?? undefined,
					})
				: isGenericGateway
					? await hybridPlayerSubmitDepositGateway({
							amount: draft.amount,
							method_type_id: getFpaySubmitMethodTypeId(draft.depositChannel),
							redirect_url: `${window.location.origin}/deposit`,
							player_bonus_id: draft.playerBonusId ?? undefined,
							bonus_config_id: draft.bonusConfigId ?? undefined,
							bank_code: needsPayerBank ? payerBankCode : undefined,
						})
					: await hybridPlayerSubmitDepositFPay({
							amount: draft.amount,
							method_type_id: getFpaySubmitMethodTypeId(draft.depositChannel),
							redirect_url: `${window.location.origin}/deposit`,
							player_bonus_id: draft.playerBonusId ?? undefined,
							bonus_config_id: draft.bonusConfigId ?? undefined,
						});
			const paymentUrl = String(res?.data?.payment_url ?? "").trim();
			if (res?.status && paymentUrl) {
				toast.success(
					res?.message ||
						(isOnempay
							? t("Redirecting to 1MPay payment page...")
							: isGenericGateway
								? t("Redirecting to payment page...")
								: t("Redirecting to FPay payment page..."))
				);
				window.open(paymentUrl, "_blank", "noopener,noreferrer");
				history.replace("/deposit");
			} else {
				toast.error(
					res?.message || t("Action Failed. Please try again later.")
				);
			}
		} catch (error: any) {
			toast.error(
				error?.response?.data?.message ||
					error?.message ||
					t("Action Failed. Please try again later.")
			);
		} finally {
			setSubmitLoading(false);
		}
	};

	return (
		<section className="main-height">
			<div className="dashboard-wrapper hybrid-deposit-page">
				<div className="hybrid-deposit-card">
					<div className="hybrid-deposit-section">
						<h6 className="hybrid-deposit-section-title">
							{isFpayRedirect
								? t("Complete your deposit")
								: t("Complete your transfer")}
						</h6>
						<p className="hybrid-deposit-hint hybrid-deposit-confirm-intro">
							{isFpayRedirect
								? t(
										"Review your payment option and amount, then tap Confirm to continue."
									)
								: t(
										"Transfer the amount below to the receiver account, then upload your receipt."
									)}
						</p>
					</div>

					<div className="hybrid-deposit-section">
						<h6 className="hybrid-deposit-section-title">
							{t("Deposit initiated")}
						</h6>
						<div className="hybrid-deposit-recipient-panel">
							<div className="hybrid-deposit-recipient-row">
								<span className="hybrid-deposit-recipient-label">
									{t("Payment option")}
								</span>
								<span className="hybrid-deposit-recipient-value">
									{channelLabel}
								</span>
							</div>
							<div className="hybrid-deposit-recipient-row">
								<span className="hybrid-deposit-recipient-label">
									{t("Deposit amount")}
								</span>
								<span className="hybrid-deposit-recipient-value hybrid-deposit-recipient-value--mono">
									{draft.amount}
								</span>
							</div>
						</div>
					</div>

					{!isBankTransfer && needsPayerBank ? (
						<div className="hybrid-deposit-section">
							<h6 className="hybrid-deposit-section-title">
								{t("Select your bank")} <span className="required">*</span>
							</h6>
							<Form.Select
								value={payerBankCode}
								onChange={(e) => setPayerBankCode(e.target.value)}
								className="hybrid-deposit-input"
								disabled={submitLoading}
							>
								<option value="">{t("Select your bank")}</option>
								{payerBanks.map((bank) => (
									<option key={bank.code} value={bank.code}>
										{bank.name}
									</option>
								))}
							</Form.Select>
						</div>
					) : null}

					{isBankTransfer ? (
						<>
							<div className="hybrid-deposit-section">
								<h6 className="hybrid-deposit-section-title">
									{t("Receiver bank account")}
								</h6>
								{receiverLoading ? (
									<div className="hybrid-deposit-hint">
										{t("Loading banks...")}
									</div>
								) : receiverError ? (
									<div className="hybrid-deposit-hint hybrid-deposit-hint--warn">
										{receiverError}
									</div>
								) : receiver ? (
									<div className="hybrid-deposit-recipient-panel">
										<div className="hybrid-deposit-recipient-row">
											<span className="hybrid-deposit-recipient-label">
												{t("Bank name")}
											</span>
											<span className="hybrid-deposit-recipient-value">
												{bankDisplayName || "-"}
											</span>
										</div>
										<div className="hybrid-deposit-recipient-row">
											<span className="hybrid-deposit-recipient-label">
												{t("Account holder name")}
											</span>
											<span className="hybrid-deposit-recipient-value">
												{receiver.receiver_bank_acc_name}
											</span>
										</div>
										<div className="hybrid-deposit-recipient-row">
											<span className="hybrid-deposit-recipient-label">
												{t("Account number")}
											</span>
											<span className="hybrid-deposit-recipient-value hybrid-deposit-recipient-value--mono">
												{receiver.receiver_bank_acc_no}
											</span>
										</div>
									</div>
								) : null}
							</div>

							<div className="hybrid-deposit-section">
								<h6 className="hybrid-deposit-section-title">
									{t("Receipt / payment proof")}{" "}
									<span className="required">*</span>
								</h6>
								<Form.Control
									ref={receiptFileInputRef}
									type="file"
									accept={RECEIPT_ACCEPT}
									onChange={handleReceiptFileChange}
									disabled={submitDisabled}
									className="hybrid-deposit-input hybrid-deposit-file-input"
								/>
								<div className="hybrid-deposit-file-meta">
									{t("PDF, JPG, or PNG - max 10 MB")}
								</div>
								{receiptFile ? (
									<div className="hybrid-deposit-receipt-preview">
										{receiptPreviewUrl ? (
											<img src={receiptPreviewUrl} alt="" />
										) : (
											<div className="hybrid-deposit-receipt-pdf">
												<i className="fa fa-file-pdf-o" aria-hidden="true" />
												<span className="hybrid-deposit-receipt-filename">
													{receiptFile.name}
												</span>
											</div>
										)}
										<button
											type="button"
											className="hybrid-deposit-clear-file"
											onClick={() => clearReceiptFile()}
											disabled={submitLoading}
										>
											{t("Remove file")}
										</button>
									</div>
								) : null}
							</div>

							<div className="hybrid-deposit-section">
								<h6 className="hybrid-deposit-section-title hybrid-deposit-optional-label">
									{t("Promo Code")}{" "}
									<span className="hybrid-deposit-optional-tag">
										({t("Optional")})
									</span>
								</h6>
								<Form.Control
									type="text"
									value={promoCode}
									onChange={(e) => setPromoCode(e.target.value)}
									placeholder={t("Enter promo code here")}
									className="hybrid-deposit-input"
									autoComplete="off"
									disabled={submitDisabled}
								/>
							</div>
						</>
					) : null}

					<div
						className={`hybrid-deposit-actions${
							isBankTransfer ? " hybrid-deposit-actions--split" : ""
						}`}
					>
						{isBankTransfer ? (
							<>
								<button
									type="button"
									className="hybrid-deposit-btn-secondary"
									disabled={submitLoading}
									onClick={() => history.push("/deposit")}
								>
									{t("Back")}
								</button>
								<button
									type="button"
									disabled={submitDisabled}
									onClick={handleSubmitDeposit}
								>
									{submitLoading ? t("Submitting...") : t("Submit")}
								</button>
							</>
						) : (
							<button
								type="button"
								disabled={
									submitLoading ||
									payerBanksLoading ||
									(needsPayerBank && !payerBankCode)
								}
								onClick={handleFpayConfirm}
							>
								{submitLoading ? t("Processing...") : t("Confirm")}
							</button>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};

export default HybridDepositConfirmPage;
